import { faAdd, faCalendar, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/page-layout";
import ChaseRenderRow from "../components/chaseListComponents/chase-render-row";
import { chaseHeadingData } from "../components/chaseListComponents/chase-list-data";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "../utils/axios-instance";
import TableWrapper from "../components/table-wrapper";
import { useDebounceValue } from "usehooks-ts";
import formQueryUrl from "../utils/form-query-url";
import PaginationMenu from "../components/pagination/paginationMenu";
import GetUser from "../utils/get-user";
import TopBarWidget from "../components/top-dashboard-widget";
import DateRangePicker from "../components/date-range-picker";
import { useRangePicker } from "../store/use-range-picker-model";
import { useChaseMenu } from "../store/use-chase-menu";

const ChaseList = () => {
  const currentUser = GetUser();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const location = window.location.pathname;
  const myCutomers = window.location.pathname === "/my-customers";
  const customerArchive = window.location.pathname === "/customer-archives";
  const chaseStatus = window.location.pathname.split("/")[2] || "";
  const { onToggleChaseMenu, onChaseMenuClose } = useChaseMenu();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useDebounceValue("", 1000);
  const [chase, setChase] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<Record<string, any>>({});
  const { onToggleRangePicker } = useRangePicker();
  const searchQuery = new URLSearchParams(window.location.search).get("query");
  const [isSameCustomerChase, setIsSameCustomerChase] = useState<string | null>(
    null
  );
  const [chaseType, setChaseType] = useState<string>("all");

  // ---------------------------------------- [ FIND CHASES ] -------------------------------
  useEffect(() => {
    const getChaseList = async () => {
      setLoading(true);
      const filtersArray = [
        { search: query },
        { status: chaseStatus },
        myCutomers && { schemeCode: currentUser?.code },
        customerArchive && { status: "Customer-Archives" },
        chaseType !== "all" && { chaseType: chaseType },
        dateRange?.startDate !== null && { startDate: dateRange?.startDate },
        dateRange?.endDate !== null && { endDate: dateRange?.endDate },
      ];
      onChaseMenuClose();
      try {
        const res = await axiosInstance.get(
          `/loan?page=${page}&limit=${pageSize}${formQueryUrl(filtersArray)}`
        );
        setChase(res.data.data);
        setTotalCount(res.data.totalCount);
        setIsSameCustomerChase(res.data.isSameCustomerChase);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    getChaseList();
  }, [
    query,
    chaseStatus,
    page,
    pageSize,
    myCutomers,
    currentUser?.code,
    dateRange,
    customerArchive,
    chaseType,
  ]);

  // -------------------------------- [ UPDATE CURRENT PAGE ] --------------------------

  const OnChangeCurrentPage = (page) => {
    setPage(page);
  };

  // -------------------------------- [ NAVIGATE TO USER ] --------------------------------

  const handleClick = () => {
    navigate("/createChase");
  };

  // ------------------------------- [ SEARCH BASED ON URL QUERY ] --------------------

  useEffect(() => {
    const updateQuery = () => {
      if (searchQuery && searchRef.current) {
        searchRef.current.value = searchQuery;
        setQuery(searchQuery);
        navigate(window.location.pathname);
      }
    };

    currentUser?.id && updateQuery();
  }, [searchQuery, navigate, setQuery, searchRef, currentUser?.id]);

  // --------------------------------- [ IF JUST ONE CHASE ITEM OPEN THAT UP ] -----------

  useEffect(() => {
    if (chase.length === 1) {
      const item = chase[0];
      onToggleChaseMenu({
        id: item?.id,
        data: {
          ...item,
          policyReference: item?.policyReference,
          apiValues: item?.apiValues,
          isSameCustomerChase: isSameCustomerChase,
        },
        timeline: item?.timeline,
        logs: item?.logs,
      });
    }
  }, [chase, isSameCustomerChase]);

  // ------------------------------ [ ON PAGE CHANGE GO BACK TO PAGE 1 ] -----------------

  useEffect(() => {
    setPage(1);
  }, [location]);

  return (
    <PageLayout
      pageTitle={
        myCutomers
          ? window.location.pathname.replace("-", " ").replace("/", "")
          : "Dashboard"
      }
      primaryAction={
        <div className="relative flex gap-3">
          <div className="flex gap-3 w-full items-center">
            <select
              onChange={(e) => setChaseType(e.target.value as any)}
              className="cursor-pointer text-center px-5 h-10 rounded-full appearance-none text-[14px] font-medium bg-surface text-text-secondary border border-edge shadow-card hover:border-edge-strong hover:text-text-primary transition-colors duration-200 outline-none"
            >
              <option value="all" disabled selected>
                Chase Type
              </option>
              <option value="all">All</option>
              <option value="arrears">Arrears</option>
              <option value="addacs">Addacs</option>
            </select>
            <div className="relative">
              <DateRangePicker onChange={(e) => setDateRange(e)}>
                <div
                  onClick={onToggleRangePicker}
                  className="rounded-full appearance-none text-[15px] flex items-center justify-center h-10 w-10 bg-surface text-light-gray border border-edge shadow-card hover:border-edge-strong hover:text-text-primary transition-colors duration-200 outline-none cursor-pointer"
                >
                  <FontAwesomeIcon icon={faCalendar} />
                </div>
              </DateRangePicker>
            </div>
            <button
              onClick={handleClick}
              disabled
              title="Creating chases is currently disabled"
              className="flex gap-2 text-[15px] font-dmSans font-[600] items-center h-10 px-5 text-white bg-gradient-to-r from-accent to-accent-bright rounded-full shadow-[0_8px_20px_-8px_rgba(99,102,241,0.65)] transition-all duration-200 disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed disabled:hover:translate-y-0 enabled:hover:shadow-[0_10px_24px_-8px_rgba(99,102,241,0.8)] enabled:hover:-translate-y-px"
            >
              <FontAwesomeIcon icon={faAdd} />
              New Chase
            </button>
          </div>
        </div>
      }
    >
      {window.location.pathname === "/my-customers" && (
        <div>
          <TopBarWidget />
        </div>
      )}

      <div className="mt-5 mr-1 bg-surface border border-edge rounded-2xl shadow-card px-6 pt-6 font-medium">
        <div className="flex w-full items-start justify-between gap-4 flex-wrap mb-5">
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold text-text-primary leading-tight">
              Chase list
            </h1>
            <p className="mt-1 text-[13px] font-medium text-light-gray capitalize">
              {chaseStatus ? chaseStatus.replace("-", " ") : "All statuses"}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-10 min-w-[290px] rounded-full flex bg-input-bg items-center border border-input-border focus-within:border-indigo-400/40 focus-within:ring-2 focus-within:ring-indigo-400/20 transition-all duration-200 px-4">
              <FontAwesomeIcon
                icon={faSearch}
                className="text-[13px] text-light-gray mr-2.5 shrink-0"
              />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search..."
                className="flex-grow bg-inherit text-[13px] text-text-primary placeholder:text-light-gray font-medium focus:outline-none disabled:opacity-50 disabled:cursor-wait"
                onChange={(e) => setQuery(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <TableWrapper
          headingData={chaseHeadingData}
          isLoading={loading}
          totalCount={totalCount}
        >
          {chase?.map((item, index) => {
            return <ChaseRenderRow item={item} index={index} key={item.id} />;
          })}
        </TableWrapper>
        <PaginationMenu
          totalCount={totalCount}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={OnChangeCurrentPage}
        />
      </div>
    </PageLayout>
  );
};

export default ChaseList;
